import React,{useState,useEffect,useCallback}from'react';
import{useNavigate,useSearchParams}from'react-router-dom';
import Layout from'../components/layout/Layout';
import api from'../services/api';
import PatientSearchInput from'../components/patient/PatientSearchInput';
import{hasAssignedModule,readStoredModules}from'../constants/modules';

const CLINIC_HOURS=(()=>{
  const times:string[]=[];
  const add=(h:number,m:number)=>times.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
  for(let h=9;h<13;h++) for(let m=0;m<60;m+=15) add(h,m);
  for(let h=14;h<18;h++) for(let m=0;m<60;m+=15) add(h,m);
  return times;
})();

const todayStr=()=>new Date().toISOString().split('T')[0];
const isPastSlot=(dateStr:string,timeStr:string)=>{
  if(!dateStr||!timeStr||dateStr!==todayStr())return false;
  const [h,m]=timeStr.split(':').map(Number);
  const slot=new Date();slot.setHours(h,m,0,0);
  return slot.getTime()<=Date.now();
};

const FLD=({label,required,children}:any)=>(
  <div><label className="block text-sm font-medium text-gray-700 mb-1">{label}{required&&<span className="text-red-500 ml-1">*</span>}</label>{children}</div>
);
const SEL=({value,onChange,children}:any)=>(
  <select value={value} onChange={onChange} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#015CA6] outline-none">{children}</select>
);
const INP=({value,onChange,type='text',placeholder='',min=''}:any)=>(
  <input type={type} value={value} onChange={onChange} placeholder={placeholder} min={min}
    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#015CA6] outline-none"/>
);

// Time slot card
const SlotCard=({time,selected,booked=0,capacity=1,overbookingLimit=0,fromRoster=false,isPast=false,onSelect}:any)=>{
  const isFull=booked>=(capacity+overbookingLimit);
  const isNearFull=!isFull&&fromRoster&&booked>=(capacity*0.8);
  const isOverbooked=booked>capacity;
  const pct=capacity>0?Math.min(100,Math.round(booked/capacity*100)):0;
  const disabled=isFull||isPast;
  return(
    <button type="button" disabled={disabled} onClick={onSelect} title={isPast?'This time has already passed today':undefined}
      className={`relative flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all
        ${isPast?'opacity-40 cursor-not-allowed border-slate-200 bg-slate-50':
          isFull?'opacity-40 cursor-not-allowed border-slate-200 bg-slate-50':
          selected?'border-[#02357A] bg-blue-50 shadow-md ring-2 ring-[#02357A]/20':
          isNearFull?'border-amber-300 bg-amber-50 hover:border-amber-400':
          'border-slate-200 bg-white hover:border-[#02357A] hover:shadow-sm'}`}>
      <span className={`text-sm font-bold ${selected?'text-[#02357A]':disabled?'text-slate-400':'text-slate-700'}`}>{time}</span>
      {isPast?(
        <span className="text-xs text-slate-400 mt-1">Past</span>
      ):fromRoster?(
        <div className="w-full mt-1.5">
          <div className="w-full bg-slate-200 rounded-full h-1 mb-1">
            <div className={`h-1 rounded-full ${isFull?'bg-red-500':isOverbooked?'bg-orange-500':isNearFull?'bg-amber-500':'bg-green-500'}`} style={{width:`${pct}%`}}/>
          </div>
          <span className={`text-xs ${isFull?'text-red-600':isOverbooked?'text-orange-600':isNearFull?'text-amber-600':'text-green-600'}`}>
            {isFull?'FULL':`${booked}/${capacity}`}
          </span>
        </div>
      ):(
        <span className="text-xs text-green-600 mt-1">Open</span>
      )}
      {selected&&!disabled&&(
        <div className="absolute -top-2 -right-2 w-5 h-5 bg-[#02357A] rounded-full flex items-center justify-center shadow">
          <span className="text-white text-xs font-bold">✓</span>
        </div>
      )}
    </button>
  );
};

export default function AppointmentBookingPage(){
  const nav=useNavigate();
  const [sp]=useSearchParams();
  const [loading,setLoading]=useState(true);
  const [doctors,setDoctors]=useState<any[]>([]);
  const [departments,setDepartments]=useState<any[]>([]);
  const [slots,setSlots]=useState<any[]>([]);
  const [slotsLoading,setSlotsLoading]=useState(false);
  const [slotsMsg,setSlotsMsg]=useState('');
  const [patient,setPatient]=useState<any>(null);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const [error,setError]=useState('');
  const [specialtyFilter,setSpecialtyFilter]=useState('');

  const [form,setForm]=useState({
    patientId:sp.get('patientId')||'',
    doctorId:sp.get('doctorId')||'',
    departmentId:'',
    appointmentDate:sp.get('date')||new Date().toISOString().split('T')[0],
    appointmentTime:'',
    consultationType:'OPD',
    reasonForVisit:'',
    chiefComplaint:'',
    notes:'',
    priority:'ROUTINE',
    referralSource:'SELF',
    referredBy:'',
    isFollowUp:false,
    paymentMode:'CASH',
    consultationFee:'',
    reminderSms:true,
    reminderWhatsapp:false,
    fastingRequired:false,
    specialInstructions:'',
  });
  const modules=readStoredModules();
  const canBook=hasAssignedModule(modules,['APPOINTMENTS','REGISTRATION','OPD']);
  const canEditRoster=hasAssignedModule(modules,'HR');
  const f=(k:string,v:any)=>setForm(x=>({...x,[k]:v}));

  const specialties=Array.from(new Set(doctors.map((d:any)=>d.specialization||d.speciality).filter(Boolean))).sort() as string[];
  const filteredDoctors=specialtyFilter?doctors.filter((d:any)=>(d.specialization||d.speciality)===specialtyFilter):doctors;

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const[d,dep]=await Promise.all([
        api.getDoctors?.().catch(()=>({data:[]})),
        api.getDepartments?.().catch(()=>({data:[]})),
      ]);
      setDoctors(Array.isArray(d?.data)?d.data:d?.data?.doctors||d?.data?.data||[]);
      setDepartments(Array.isArray(dep?.data)?dep.data:dep?.data?.data||[]);
    }catch{}
    if(sp.get('patientId')){
      api.getPatientById?.(sp.get('patientId')!).then((r:any)=>r?.data&&setPatient(r.data)).catch(()=>{});
    }
    setLoading(false);
  },[sp]);

  useEffect(()=>{load();},[load]);

  // Load slots when doctor + date changes
  useEffect(()=>{
    if(!form.doctorId||!form.appointmentDate){setSlots([]);setSlotsMsg('');return;}
    let cancelled=false;
    const clinicFallback=(msg:string)=>{
      if(cancelled) return;
      setSlots(CLINIC_HOURS.map(time=>({time,booked:0,capacity:1,overbookingLimit:0,fromRoster:false})));
      setSlotsMsg(msg);
    };
    const mapRoster=(list:any[])=>list.map((s:any)=>({
      time:(s.slot_time||s.slotTime||s.time||'').toString().slice(0,5),
      booked:+(s.booked||0),capacity:+(s.capacity||1),
      overbookingLimit:+(s.overbooking_limit||s.overbookingLimit||0),
      fromRoster:true,
    })).filter((s:any)=>s.time);
    const mapSched=(list:any[])=>list.map((s:any)=>({
      time:(s.start_time||s.slotTime||s.time||String(s)).toString().slice(0,5),
      booked:s.available===false?1:0,capacity:1,overbookingLimit:0,fromRoster:false,
    })).filter((s:any)=>s.time);

    setSlotsLoading(true);setSlots([]);f('appointmentTime','');
    (async()=>{
      try{
        const r=await api.getRosterAvailableSlots?.(form.doctorId,form.appointmentDate);
        const list=Array.isArray(r?.data)?r.data:r?.data?.slots||r?.data?.data||[];
        if(!cancelled && list.length>0){
          setSlots(mapRoster(list));
          setSlotsMsg('');
          return;
        }
      }catch(e:any){
        if(e?.response?.status===403){
          clinicFallback(canEditRoster
            ? 'Could not read duty roster. Standard OPD hours are shown.'
            : 'Duty roster is set by HR. Your OPD / Registration access is enough to book — standard clinic hours (9 AM–1 PM, 2–6 PM) are shown.');
          return;
        }
      }
      try{
        const r2=await api.getDoctorSlots?.(form.doctorId,form.appointmentDate);
        const list2=Array.isArray(r2?.data)?r2.data:r2?.data?.slots||r2?.data?.data||[];
        if(!cancelled && list2.length>0){
          setSlots(mapSched(list2));
          setSlotsMsg('');
          return;
        }
      }catch{}
      clinicFallback('No published roster for this doctor on this date. Standard OPD hours are shown — pick a time to book.');
    })().finally(()=>{if(!cancelled) setSlotsLoading(false);});
    return()=>{cancelled=true;};
  },[form.doctorId,form.appointmentDate,canEditRoster]);

  // Auto-fill consultation fee from doctor
  useEffect(()=>{
    if(!form.doctorId)return;
    const doc=doctors.find((d:any)=>d.id===form.doctorId);
    if(doc){
      const fee=doc.consultationFee||doc.consultation_fee||'';
      if(fee)f('consultationFee',String(fee));
    }
  },[form.doctorId,doctors]);

  const submit=async()=>{
    if(!form.patientId){setError('Please select a patient');return;}
    if(!form.doctorId){setError('Please select a doctor');return;}
    if(!form.appointmentDate){setError('Please select a date');return;}
    if(!form.appointmentTime){setError('Please select a time slot');return;}
    if(form.appointmentDate<todayStr()){setError('Cannot book an appointment in the past');return;}
    if(isPastSlot(form.appointmentDate,form.appointmentTime)){setError('That time has already passed today — please pick a later slot');return;}
    setSaving(true);setError('');
    try{
      await api.createAppointment?.({
        patientId:form.patientId,doctorId:form.doctorId,
        departmentId:form.departmentId||null,
        appointmentDate:form.appointmentDate,
        appointmentTime:form.appointmentTime,
        consultationType:form.consultationType,
        reasonForVisit:form.reasonForVisit||null,
        chiefComplaint:form.chiefComplaint||null,
        notes:form.notes||null,priority:form.priority,
        referralSource:form.referralSource||null,
        referredBy:form.referredBy||null,
        isFollowUp:form.isFollowUp,
        paymentMode:form.paymentMode,
        consultationFee:form.consultationFee?parseFloat(form.consultationFee):null,
        reminderSms:form.reminderSms,
        reminderWhatsapp:form.reminderWhatsapp,
        fastingRequired:form.fastingRequired,
        specialInstructions:form.specialInstructions||null,
      });
      setMsg('✅ Appointment booked successfully!');
      setTimeout(()=>nav('/appointments'),1500);
    }catch(e:any){setError('❌ '+(e.response?.data?.message||'Booking failed. Please try again.'));}
    setSaving(false);
  };

  return(
    <Layout>
      <div className="p-6 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Book Appointment</h1>
            <p className="text-gray-500 text-sm mt-1">Schedule a new patient appointment</p>
            <p className="text-xs text-slate-500 mt-1">
              {canBook
                ? (canEditRoster
                  ? 'You can book visits and also edit the duty roster.'
                  : 'Your access (OPD / Registration) is enough to book. Duty Roster (HR) is only needed to publish doctor shifts — not to book.')
                : 'Booking needs OPD, Registration, or Appointments on your role. Ask an admin to tick one of those in Settings → Users.'}
            </p>
          </div>
          <button onClick={()=>nav('/appointments')} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50 text-gray-600">← Back</button>
        </div>

        {error&&<div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
        {msg&&<div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm font-semibold">{msg}</div>}

        <div className="space-y-4">

          {/* Patient */}
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <h3 className="font-bold text-gray-800 mb-4 pb-2 border-b">Patient</h3>
            <PatientSearchInput
              label="Search Patient *"
              selected={patient}
              onSelect={(p:any)=>{setPatient(p);f('patientId',p.id);}}
              onClear={()=>{setPatient(null);f('patientId','');}}
              placeholder="Search by name, UHID or phone..."
            />
          </div>

          {/* Doctor & Department */}
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <h3 className="font-bold text-gray-800 mb-4 pb-2 border-b">Doctor & Schedule</h3>
            <div className="grid grid-cols-2 gap-4">
              <FLD label="Filter by Specialty">
                <SEL value={specialtyFilter} onChange={(e:any)=>{setSpecialtyFilter(e.target.value);f('doctorId','');}}>
                  <option value="">All Departments</option>
                  {specialties.map(s=><option key={s} value={s}>{s}</option>)}
                </SEL>
              </FLD>
              <FLD label="Doctor" required>
                <SEL value={form.doctorId} onChange={(e:any)=>f('doctorId',e.target.value)}>
                  <option value="">Select Doctor</option>
                  {filteredDoctors.map((d:any)=>(
                    <option key={d.id} value={d.id}>
                      Dr. {d.firstName||d.first_name} {d.lastName||d.last_name}
                      {(d.specialization||d.speciality)?` — ${d.specialization||d.speciality}`:''}
                    </option>
                  ))}
                </SEL>
              </FLD>
              <FLD label="Appointment Date" required>
                <INP type="date" value={form.appointmentDate} onChange={(e:any)=>f('appointmentDate',e.target.value)} min={new Date().toISOString().split('T')[0]}/>
              </FLD>
              <FLD label="Department">
                <SEL value={form.departmentId} onChange={(e:any)=>f('departmentId',e.target.value)}>
                  <option value="">All Departments</option>
                  {departments.map((d:any)=><option key={d.id} value={d.id}>{d.department_name||d.departmentName||d.name}</option>)}
                </SEL>
              </FLD>
            </div>

            {/* TIME SLOT CARDS */}
            {(form.doctorId&&form.appointmentDate)&&(
              <div className="mt-5">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-700">Select Time Slot <span className="text-red-500">*</span></label>
                  {form.appointmentTime&&(
                    <span className="text-sm font-bold text-[#02357A] bg-blue-50 px-3 py-1 rounded-lg border border-blue-200">
                      ⏰ {form.appointmentTime} selected
                    </span>
                  )}
                </div>

                {slotsLoading?(
                  <div className="grid grid-cols-5 gap-2">
                    {[...Array(10)].map((_,i)=>(
                      <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse"/>
                    ))}
                  </div>
                ):slots.length>0?(
                  <>
                    {slotsMsg&&<p className="text-xs text-amber-600 mb-2">⚠️ {slotsMsg}</p>}
                    <div className="grid grid-cols-5 gap-2">
                      {slots.map((s:any)=>(
                        <SlotCard key={s.time} {...s}
                          selected={form.appointmentTime===s.time}
                          isPast={isPastSlot(form.appointmentDate,s.time)}
                          onSelect={()=>f('appointmentTime',form.appointmentTime===s.time?'':s.time)}/>
                      ))}
                    </div>
                  </>
                ):(
                  <>
                    {slotsMsg&&<p className="text-xs text-slate-500 mb-3">ℹ️ {slotsMsg}</p>}
                    <div className="grid grid-cols-5 gap-2">
                      {CLINIC_HOURS.map(t=>(
                        <SlotCard key={t} time={t} selected={form.appointmentTime===t} fromRoster={false}
                          isPast={isPastSlot(form.appointmentDate,t)}
                          onSelect={()=>f('appointmentTime',form.appointmentTime===t?'':t)}/>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Appointment Details */}
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <h3 className="font-bold text-gray-800 mb-4 pb-2 border-b">Appointment Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <FLD label="Consultation Type">
                <SEL value={form.consultationType} onChange={(e:any)=>f('consultationType',e.target.value)}>
                  {['OPD','FOLLOW_UP','TELECONSULT','EMERGENCY','REVIEW','SECOND_OPINION'].map(t=><option key={t}>{t}</option>)}
                </SEL>
              </FLD>
              <FLD label="Priority">
                <SEL value={form.priority} onChange={(e:any)=>f('priority',e.target.value)}>
                  {['ROUTINE','URGENT','EMERGENCY'].map(p=><option key={p}>{p}</option>)}
                </SEL>
              </FLD>
              <div className="col-span-2">
                <FLD label="Reason for Visit">
                  <INP value={form.reasonForVisit} onChange={(e:any)=>f('reasonForVisit',e.target.value)} placeholder="Chief complaint or reason for appointment"/>
                </FLD>
              </div>
              <div className="col-span-2">
                <FLD label="Chief Complaint">
                  <INP value={form.chiefComplaint} onChange={(e:any)=>f('chiefComplaint',e.target.value)} placeholder="Primary complaint in patient's words"/>
                </FLD>
              </div>
              <div className="col-span-2">
                <FLD label="Additional Notes">
                  <textarea value={form.notes} onChange={e=>f('notes',e.target.value)} rows={2}
                    placeholder="Any additional information for the doctor…"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#015CA6] outline-none"/>
                </FLD>
              </div>
              <FLD label="Referral Source">
                <SEL value={form.referralSource} onChange={(e:any)=>f('referralSource',e.target.value)}>
                  {['SELF','DOCTOR_REFERRAL','CAMP','ONLINE','INSURANCE','CORPORATE','OTHER'].map(s=><option key={s}>{s}</option>)}
                </SEL>
              </FLD>
              <FLD label="Referred By">
                <INP value={form.referredBy} onChange={(e:any)=>f('referredBy',e.target.value)} placeholder="Dr. Name / Hospital"/>
              </FLD>
              <div className="col-span-2">
                <FLD label="Special Instructions">
                  <INP value={form.specialInstructions} onChange={(e:any)=>f('specialInstructions',e.target.value)} placeholder="e.g. Bring previous reports, fasting required…"/>
                </FLD>
              </div>
              <div className="col-span-2 flex flex-wrap gap-5 pt-1">
                {[['isFollowUp','Follow-up Visit'],['fastingRequired','Fasting Required'],['reminderSms','SMS Reminder'],['reminderWhatsapp','WhatsApp Reminder']].map(([k,l])=>(
                  <label key={k} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={(form as any)[k]} onChange={e=>f(k,e.target.checked)} className="w-4 h-4 rounded accent-[#02357A]"/>
                    <span className="text-sm font-medium text-gray-700">{l}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Payment */}
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <h3 className="font-bold text-gray-800 mb-4 pb-2 border-b">Consultation Fee</h3>
            <div className="grid grid-cols-2 gap-4">
              <FLD label="Payment Mode">
                <SEL value={form.paymentMode} onChange={(e:any)=>f('paymentMode',e.target.value)}>
                  {['CASH','CARD','UPI','INSURANCE','CORPORATE','FREE'].map(m=><option key={m}>{m}</option>)}
                </SEL>
              </FLD>
              <FLD label="Consultation Fee (₹)">
                <INP type="number" value={form.consultationFee} onChange={(e:any)=>f('consultationFee',e.target.value)} placeholder="500"/>
              </FLD>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end pb-6">
            <button onClick={()=>nav('/appointments')} className="px-6 py-2.5 border rounded-xl hover:bg-gray-50 text-gray-600">Cancel</button>
            <button onClick={submit} disabled={saving||!form.patientId||!form.doctorId||!form.appointmentTime}
              className="px-8 py-2.5 bg-[#02357A] text-white rounded-xl hover:bg-[#015CA6] font-semibold disabled:opacity-50 min-w-44">
              {saving?'Booking…':'✓ Book Appointment'}
            </button>
          </div>
          {!form.appointmentTime&&form.doctorId&&form.appointmentDate&&(
            <p className="text-xs text-red-500 text-center -mt-4">⚠️ Please select a time slot to proceed</p>
          )}
        </div>
      </div>
    </Layout>
  );
}
